import { useState, useRef, useEffect } from "react";
import { useTheme } from "../components/ThemeProvider";
import { useAuth } from "../context/AuthContext";
import { dialogs } from "../context/DialogContext";
import { useCarla } from "../hooks/useCarla";
import CarlaImg from "../assets/Carla-removebg.png";

// ── CONSTANTES ────────────────────────────────────────────────────────────
const GOLD = "#c9a84c";
const GOLD_LIGHT = "rgba(201,168,76,0.13)";
const TABS = ["Chat", "Alertas", "Tickets", "Contratos", "Base de conocimiento"];

const CATEGORY_LABELS = {
  ley: "Ley", rd: "Real Decreto", decreto: "Decreto", reglamento: "Reglamento",
  circular: "Circular", resolucion: "Resolución",
  fiscal: "Fiscal", matrimonial: "Matrimonial", contrato: "Contrato", articulo: "Artículo",
};
const CATEGORY_ICONS = {
  ley: "⚖️", rd: "📜", decreto: "📜", reglamento: "📋",
  circular: "🔄", resolucion: "📝",
  fiscal: "💰", matrimonial: "💑", contrato: "📄", articulo: "📰",
};
const DOC_FILTERS = [
  { id: "all",        label: "Todos",          icon: "🗂️" },
  { id: "ley",        label: "Leyes",          icon: "⚖️" },
  { id: "rd",         label: "Real Decreto",   icon: "📜" },
  { id: "decreto",    label: "Decreto",        icon: "📜" },
  { id: "reglamento", label: "Reglamentos",    icon: "📋" },
  { id: "circular",   label: "Circulares",     icon: "🔄" },
  { id: "resolucion", label: "Resoluciones",   icon: "📝" },
  { id: "fiscal",     label: "Fiscal",         icon: "💰" },
  { id: "matrimonial",label: "Matrimonial",    icon: "💑" },
  { id: "contrato",   label: "Contratos",      icon: "📄" },
  { id: "articulo",   label: "Artículos",      icon: "📰" },
];
const CONFIDENCE_COLORS = { green: "#22c55e", yellow: "#eab308", red: "#ef4444" };
const CONFIDENCE_LABELS = {
  green: "🟢 Verificado", yellow: "🟡 Probable", red: "🔴 No confirmado",
};
const CONTRACT_TYPES = ["arras", "compraventa", "alquiler", "gestion", "exclusividad"];
const CONTRACT_LANGS_BY_TYPE = {
  arras:        ["ES", "EN", "DE", "FR", "NL", "RU"],
  compraventa:  ["ES", "EN", "DE", "FR", "NL", "RU"],
  alquiler:     ["ES", "EN", "DE", "FR", "NL", "RU"],
  gestion:      ["ES", "EN", "DE"],
  exclusividad: ["ES", "EN", "DE"],
};
const LANG_LABELS = { ES: "Español", EN: "English", DE: "Deutsch", FR: "Français", NL: "Nederlands", RU: "Русский" };
const PRIORITIES = ["alta", "media", "baja"];
const TICKET_CATS = ["fiscal", "matrimonial", "arrendamiento", "compraventa", "arras", "urbanismo", "general"];

function horaActual() {
  return new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
}

// Renderiza el texto de Carla realzando indicadores de confianza y el disclaimer
function RenderMensajeCarla({ text, isDark }) {
  const lines = text.split("\n");
  return (
    <div style={{ fontSize: 13, lineHeight: 1.7 }}>
      {lines.map((line, i) => {
        const isVerificado   = line.includes("🟢");
        const isProbable     = line.includes("🟡");
        const isNoConfirmado = line.includes("🔴");
        const isDisclaimer   = line.startsWith("📋") || line.startsWith("---");
        const isSeparator    = line.trim() === "---";

        if (isSeparator) return <hr key={i} style={{ border: "none", borderTop: `1px solid ${GOLD}33`, margin: "8px 0" }} />;

        const lineStyle = {
          display: "block",
          padding: isVerificado || isProbable || isNoConfirmado || isDisclaimer ? "2px 8px" : "0",
          borderRadius: 4,
          marginBottom: isVerificado || isProbable || isNoConfirmado || isDisclaimer ? 2 : 0,
          background: isVerificado
            ? "rgba(34,197,94,0.10)"
            : isProbable
            ? "rgba(234,179,8,0.10)"
            : isNoConfirmado
            ? "rgba(239,68,68,0.10)"
            : isDisclaimer
            ? (isDark ? "rgba(201,168,76,0.08)" : "rgba(201,168,76,0.06)")
            : "transparent",
          borderLeft: isVerificado
            ? "3px solid #22c55e"
            : isProbable
            ? "3px solid #eab308"
            : isNoConfirmado
            ? "3px solid #ef4444"
            : isDisclaimer
            ? `3px solid ${GOLD}88`
            : "none",
          color: isDisclaimer
            ? GOLD
            : undefined,
          fontSize: isDisclaimer ? 11 : undefined,
          fontStyle: isDisclaimer ? "italic" : undefined,
        };

        return <span key={i} style={lineStyle}>{line || "\u00A0"}<br /></span>;
      })}
    </div>
  );
}

// ── ALERTA CARD ───────────────────────────────────────────────────────────
function AlertCard({ alert, isDark, border, onAskCarla }) {
  const urgencyColor = alert.urgency === "alta" ? "#ef4444" : alert.urgency === "media" ? "#eab308" : "#22c55e";
  return (
    <div style={{ borderBottom: `1px solid ${border}`, padding: "14px 20px", borderLeft: `3px solid ${urgencyColor}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 4 }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: isDark ? "#e2e8f0" : "#0f172a", flex: 1 }}>{alert.title}</span>
        <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
          <span style={{ background: urgencyColor + "22", color: urgencyColor, borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 700 }}>{alert.urgency}</span>
          <span style={{ background: GOLD_LIGHT, color: GOLD, borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 600 }}>{alert.type}</span>
        </div>
      </div>
      {alert.description && <p style={{ fontSize: 12, color: isDark ? "#94a3b8" : "#475569", margin: 0, lineHeight: 1.5 }}>{alert.description}</p>}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, gap: 8 }}>
        <div style={{ display: "flex", gap: 14, fontSize: 11, color: isDark ? "#475569" : "#94a3b8" }}>
          {alert.effective_date && <span>📅 {new Date(alert.effective_date).toLocaleDateString("es-ES")}</span>}
          {alert.affected_ops > 0 && <span>🏠 {alert.affected_ops} operaciones</span>}
        </div>
        {onAskCarla && (
          <button
            onClick={() => onAskCarla(`Sobre la alerta "${alert.title}": ${alert.description || ""}`)}
            style={{
              background: "transparent", border: `1px solid ${GOLD}88`, color: GOLD,
              borderRadius: 20, padding: "3px 12px", fontSize: 11, fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
              transition: "all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = GOLD; e.currentTarget.style.color = "#fff"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = GOLD; }}
          >
            ⚖️ Preguntar a Carla
          </button>
        )}
      </div>
    </div>
  );
}

// ── BURBUJA CARLA (respuestas en columna derecha) ─────────────────────────
function RespuestaCarla({ msg, isDark, border, textPrimary, textMuted }) {
  return (
    <div style={{ marginBottom: 20, paddingBottom: 20, borderBottom: `1px solid ${border}` }}>
      <div style={{ fontSize: 11, color: GOLD, marginBottom: 6 }}>
        {msg.created_at ? new Date(msg.created_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) : horaActual()}
      </div>
      <div style={{ fontSize: 15, color: textPrimary, lineHeight: 1.8 }}>{msg.content}</div>
      {msg.sources?.length > 0 && (
        <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 4 }}>
          {msg.sources.map((s, i) => (
            <span key={i} style={{ background: GOLD_LIGHT, color: GOLD, borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 600 }}>
              📄 {s.title?.substring(0, 28)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── TICKET ROW ────────────────────────────────────────────────────────────
function TicketRow({ ticket, isDark, border, textPrimary, textMuted, onClick }) {
  const statusColors = { pendiente: "#eab308", "en proceso": "#3b82f6", respondido: "#22c55e", derivado: "#f97316" };
  const st = ticket.status || "pendiente";
  return (
    <div onClick={onClick} style={{ borderBottom: `1px solid ${border}`, padding: "12px 20px", cursor: "pointer", transition: "background 0.15s" }}
      onMouseEnter={e => { e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 12, color: GOLD }}>{ticket.ticket_ref}</span>
        <div style={{ display: "flex", gap: 5 }}>
          {ticket.priority && <span style={{ fontSize: 10, fontWeight: 600, color: ticket.priority === "alta" ? "#ef4444" : ticket.priority === "media" ? "#eab308" : "#22c55e", background: (ticket.priority === "alta" ? "#ef444422" : ticket.priority === "media" ? "#eab30822" : "#22c55e22"), padding: "2px 7px", borderRadius: 20 }}>{ticket.priority}</span>}
          <span style={{ fontSize: 10, fontWeight: 700, color: statusColors[st] || "#94a3b8", background: (statusColors[st] || "#94a3b8") + "22", padding: "2px 7px", borderRadius: 20 }}>{st}</span>
        </div>
      </div>
      <p style={{ margin: "4px 0 0", fontSize: 12, color: isDark ? "#cbd5e1" : "#334155", lineHeight: 1.4 }}>{ticket.subject}</p>
      <div style={{ marginTop: 4, fontSize: 10, color: textMuted }}>
        {ticket.agent_name} · {new Date(ticket.created_at).toLocaleDateString("es-ES")}
        {ticket.category && <span style={{ marginLeft: 8, background: GOLD_LIGHT, color: GOLD, padding: "1px 5px", borderRadius: 20 }}>{ticket.category}</span>}
      </div>
    </div>
  );
}

// ── DOCUMENT ROW ──────────────────────────────────────────────────────────
function DocumentRow({ doc, isDark, border, textPrimary, textMuted }) {
  const [expanded, setExpanded] = useState(false);
  const confColor = CONFIDENCE_COLORS[doc.confidence] || "#94a3b8";
  const icon = CATEGORY_ICONS[doc.category] || "📄";
  const bgHover = isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)";
  const bgExpanded = isDark ? "rgba(201,168,76,0.06)" : "rgba(201,168,76,0.04)";

  return (
    <div style={{ borderBottom: `1px solid ${border}` }}>
      {/* Fila principal — clicable */}
      <div
        onClick={() => setExpanded(p => !p)}
        style={{
          padding: "12px 20px", display: "flex", gap: 12, alignItems: "flex-start",
          cursor: "pointer", transition: "background 0.15s",
          background: expanded ? bgExpanded : "transparent",
        }}
        onMouseEnter={e => { if (!expanded) e.currentTarget.style.background = bgHover; }}
        onMouseLeave={e => { if (!expanded) e.currentTarget.style.background = "transparent"; }}
      >
        <div style={{ width: 34, height: 34, borderRadius: 8, background: GOLD_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 15 }}>{icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, fontSize: 12, color: isDark ? "#e2e8f0" : "#0f172a", flex: 1 }}>{doc.title}</span>
            <div style={{ display: "flex", gap: 5, flexShrink: 0, alignItems: "center" }}>
              {doc.is_own && <span style={{ background: GOLD_LIGHT, color: GOLD, fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 20 }}>VITALCASA</span>}
              <span style={{ background: confColor + "22", color: confColor, fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 20 }}>{CONFIDENCE_LABELS[doc.confidence]}</span>
              {/* Enlace fuente — solo icono, abre en nueva pestaña sin propagar el click al expand */}
              {doc.source_url && (
                <a
                  href={doc.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Abrir fuente oficial"
                  onClick={e => e.stopPropagation()}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    width: 24, height: 24, borderRadius: 6,
                    border: `1px solid ${GOLD}55`, color: GOLD,
                    background: GOLD_LIGHT, textDecoration: "none", fontSize: 12,
                    transition: "all 0.15s", flexShrink: 0,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = GOLD; e.currentTarget.style.color = "#fff"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = GOLD_LIGHT; e.currentTarget.style.color = GOLD; }}
                >
                  ↗
                </a>
              )}
              {/* Chevron expand */}
              <span style={{ color: textMuted, fontSize: 11, transition: "transform 0.2s", display: "inline-block", transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}>▾</span>
            </div>
          </div>
          <p style={{ margin: "3px 0 0", fontSize: 11, color: textMuted, lineHeight: 1.4,
            overflow: "hidden", display: "-webkit-box", WebkitLineClamp: expanded ? "unset" : 2, WebkitBoxOrient: "vertical" }}>
            {doc.summary}
          </p>
          <div style={{ marginTop: 4, display: "flex", gap: 8, fontSize: 10, color: textMuted, flexWrap: "wrap" }}>
            <span>{doc.source}</span>
            {doc.doc_date && <span>📅 {new Date(doc.doc_date).toLocaleDateString("es-ES")}</span>}
            {doc.pages && <span>{doc.pages} págs.</span>}
            {doc.lang && <span>🌐 {doc.lang}</span>}
            <span style={{ background: isDark ? "#1e2230" : "#e2e8f0", padding: "1px 5px", borderRadius: 8 }}>{CATEGORY_LABELS[doc.category] || doc.category}</span>
            {doc.has_alert && <span style={{ color: "#eab308" }}>⚠️ {doc.alert_text}</span>}
          </div>
        </div>
      </div>

      {/* Panel expandido */}
      {expanded && (
        <div style={{ padding: "0 20px 16px 66px", background: bgExpanded, borderTop: `1px solid ${GOLD}22` }}>
          {doc.full_text ? (
            <div style={{ fontSize: 12, color: textPrimary, lineHeight: 1.8, whiteSpace: "pre-wrap", maxHeight: 320, overflowY: "auto", paddingTop: 12 }}>
              {doc.full_text}
            </div>
          ) : (
            <div style={{ paddingTop: 12 }}>
              <p style={{ fontSize: 12, color: textPrimary, lineHeight: 1.8, margin: "0 0 12px" }}>{doc.summary}</p>
              {doc.source_url && (
                <a
                  href={doc.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    background: GOLD, color: "#fff", borderRadius: 8,
                    padding: "6px 14px", fontSize: 11, fontWeight: 700,
                    textDecoration: "none", transition: "opacity 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = "0.85"; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                >
                  ↗ Abrir en {doc.source}
                </a>
              )}
              {!doc.source_url && doc.is_own && (
                <span style={{ fontSize: 11, color: GOLD, background: GOLD_LIGHT, padding: "4px 10px", borderRadius: 8 }}>
                  📁 Documento interno Vitalcasa
                </span>
              )}
              {!doc.source_url && !doc.is_own && (
                <span style={{ fontSize: 11, color: textMuted }}>Sin enlace externo disponible</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── PAGE PRINCIPAL ────────────────────────────────────────────────────────
export default function CarlaPage() {
  const { theme } = useTheme();
  const { usuario } = useAuth();
  const isDark = theme.name === "dark";
  const nombreCorto = (usuario?.nombre || usuario?.email?.split('@')[0] || '').split(' ')[0];
  const [seedStatus, setSeedStatus] = useState(null);
  const [seedLoading, setSeedLoading] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  const {
    alerts, documents, tickets, chatHistory, contracts,
    loading, chatLoading,
    sendMessage, clearChat, createTicket, generateContract,
    runSeed, runSearchNow, getStatus, runAudit, runAutoAudit, uploadPdf, loadDocuments, loadAlerts,
  } = useCarla();

  const handleSeed = async () => {
    setSeedLoading(true);
    setSeedStatus(null);
    const result = await runSeed();
    setSeedStatus(result?.msg || (result?.ok ? "✅ Completado" : "❌ Error"));
    setSeedLoading(false);
  };

  const handleSearchNow = async () => {
    setSeedLoading(true);
    setSeedStatus("🔍 Buscando en BOE y DOGV...");
    const result = await runSearchNow();
    setSeedStatus(result?.msg || "Búsqueda iniciada en background — espera unos segundos y recarga");
    setSeedLoading(false);
    setTimeout(() => { loadDocuments(); loadAlerts(); }, 8000);
  };

  const handleAudit = async () => {
    setAuditLoading(true);
    setAuditResult(null);
    const result = await runAudit();
    setAuditResult(result);
    setAuditLoading(false);
  };

  const [tab, setTab] = useState("Chat");
  const [chatInput, setChatInput] = useState("");

  const handleAskFromAlert = (context) => {
    setTab("Chat");
    setChatInput(context);
    setTimeout(() => inputRef.current?.focus(), 100);
  };
  const chatRef = useRef(null);       // contenedor de scroll
  const lastCarlaRef = useRef(null);  // último mensaje de Carla
  const inputRef = useRef(null);

  // Ticket form
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [ticketForm, setTicketForm] = useState({ subject: "", category: "general", priority: "media" });
  const [ticketError, setTicketError] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);

  // Contract form
  const [contractForm, setContractForm] = useState({ type: "arras", lang: "ES", partyA: "", partyB: "", price: "", propertyDesc: "", signDate: "" });
  const [contractResult, setContractResult] = useState(null);
  const [contractError, setContractError] = useState("");

  // Doc filter
  const [docCategory, setDocCategory] = useState("all");

  // System status panel
  const [systemStatus, setSystemStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [showStatusPanel, setShowStatusPanel] = useState(false);
  const [autoAuditMsg, setAutoAuditMsg] = useState(null);

  const loadSystemStatus = async () => {
    setStatusLoading(true);
    const s = await getStatus();
    setSystemStatus(s);
    setStatusLoading(false);
  };

  const handleAutoAudit = async () => {
    setAutoAuditMsg("🔄 Auditoría iniciada...");
    const r = await runAutoAudit();
    setAutoAuditMsg(r?.ok ? "✅ Auditoría en proceso — revisa alertas en unos minutos" : `❌ ${r?.msg}`);
    setTimeout(() => setAutoAuditMsg(null), 8000);
  };

  // PDF upload
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadForm, setUploadForm] = useState({ title: "", category: "ley", source: "", lang: "ES", doc_date: "", is_own: false });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);

  const handleUploadPdf = async () => {
    if (!uploadFile) { setUploadMsg("❌ Selecciona un archivo PDF"); return; }
    if (!uploadForm.title.trim()) { setUploadMsg("❌ El título es obligatorio"); return; }
    setUploadLoading(true);
    setUploadMsg(null);
    try {
      const result = await uploadPdf(uploadFile, uploadForm);
      setUploadMsg(`✅ "${result.title}" subido e indexado correctamente`);
      setUploadFile(null);
      setUploadForm({ title: "", category: "ley", source: "", lang: "ES", doc_date: "", is_own: false });
      setTimeout(() => { setShowUploadForm(false); setUploadMsg(null); }, 3000);
    } catch (e) {
      setUploadMsg(`❌ ${e.message}`);
    } finally {
      setUploadLoading(false);
    }
  };

  // Colors
  const bgPage   = theme.colors.bgApp;
  const bgPanel  = theme.colors.bgPanel;
  const border   = theme.colors.border;
  const textPrimary = theme.colors.textPrimary;
  const textMuted   = theme.colors.textMuted;

  const inputBg     = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)";

  const cssGlobal = `
    @keyframes bounce { 0%,80%,100%{transform:translateY(0);} 40%{transform:translateY(-6px);} }
    @keyframes pulse  { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:0.5;transform:scale(1.3);} }
    .dot-gold { animation: bounce 1.2s infinite; }
    .dot-gold:nth-child(2) { animation-delay: 0.15s; }
    .dot-gold:nth-child(3) { animation-delay: 0.3s; }
  `;

  useEffect(() => {
    if (chatLoading) {
      // Mientras espera respuesta: scroll al final para ver el indicador de carga
      if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
    } else if (lastCarlaRef.current) {
      // Cuando llega la respuesta: scroll al INICIO del último mensaje de Carla
      lastCarlaRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [chatHistory, chatLoading]);

  const handleSend = async (override) => {
    // override solo es válido si es un string, no un evento de React
    const msg = (typeof override === 'string' ? override : chatInput).trim();
    if (!msg || chatLoading) return;
    setChatInput("");
    await sendMessage(msg);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleCreateTicket = async () => {
    if (!ticketForm.subject.trim()) { setTicketError("El asunto es obligatorio"); return; }
    setTicketError("");
    try {
      await createTicket(ticketForm.subject, ticketForm.category, ticketForm.priority);
      setTicketForm({ subject: "", category: "general", priority: "media" });
      setShowTicketForm(false);
    } catch (e) { setTicketError(e.message); }
  };

  const handleGenerateContract = async () => {
    if (!contractForm.partyA || !contractForm.partyB) { setContractError("Las partes son obligatorias"); return; }
    setContractError("");
    setContractResult(null);
    try {
      const result = await generateContract({ ...contractForm });
      setContractResult(result);
    } catch (e) { setContractError(e.message); }
  };

  const filteredDocs = docCategory === "all" ? documents : documents.filter(d => d.category === docCategory);

  const inputStyle = {
    background: inputBg, border: `1px solid ${border}`, borderRadius: 8,
    padding: "8px 12px", fontSize: 13, color: textPrimary, outline: "none",
    fontFamily: "inherit", width: "100%", boxSizing: "border-box",
  };
  const btnGold = {
    background: GOLD, color: "#fff", border: "none", borderRadius: 8,
    padding: "7px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer",
    fontFamily: "inherit", transition: "opacity 0.15s",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 57px)", background: bgPage, overflow: "hidden" }}>
      <style>{cssGlobal}</style>

      {/* ══ HEADER ══════════════════════════════════════════════ */}
      <div style={{
        height: 62, minHeight: 62, flexShrink: 0,
        background: bgPanel, borderBottom: `1px solid ${border}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 20px", zIndex: 30,
      }}>
        {/* Identidad */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 38, height: 38, borderRadius: "50%", overflow: "hidden", border: `2px solid ${GOLD}55`, flexShrink: 0 }}>
            <img src={CarlaImg} alt="Carla" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: textPrimary }}>Carla</div>
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e" }} />
              <div style={{ fontSize: 11, color: textMuted }}>Asesora Jurídica IA · En línea</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 2 }}>
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: "5px 14px", borderRadius: 20, border: "none",
              cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: tab === t ? 700 : 500,
              background: tab === t ? GOLD : "transparent",
              color: tab === t ? "#fff" : textMuted,
              transition: "all 0.15s", position: "relative",
            }}>
              {t}
              {t === "Alertas" && alerts.filter(a => a.urgency === "alta").length > 0 && (
                <span style={{ position: "absolute", top: -2, right: -2, width: 8, height: 8, borderRadius: "50%", background: "#ef4444" }} />
              )}
              {t === "Tickets" && tickets.filter(tk => tk.status === "pendiente").length > 0 && (
                <span style={{ position: "absolute", top: -2, right: -2, width: 8, height: 8, borderRadius: "50%", background: "#eab308" }} />
              )}
            </button>
          ))}
        </div>

        {/* Acciones */}
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {tab === "Chat" && (
            <button onClick={clearChat} title="Nueva sesión" style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${border}`, background: "transparent", color: textMuted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.52"/>
              </svg>
            </button>
          )}
          {tab === "Tickets" && (
            <button onClick={() => setShowTicketForm(p => !p)} style={btnGold}>+ Nuevo</button>
          )}
        </div>
      </div>

      {/* ══ CUERPO ══════════════════════════════════════════════ */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0 }}>

        {/* ── TAB CHAT — estructura idéntica a Felipe ── */}
        {tab === "Chat" && (
          <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>

            {/* Imagen Carla — 50% ancho */}
            <div style={{ width: "50%", flexShrink: 0, position: "relative", overflow: "hidden", borderRight: `1px solid ${border}` }}>
              <img
                src={CarlaImg}
                alt="Carla"
                style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }}
              />
              <div style={{
                position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)",
                background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)",
                border: "1px solid rgba(255,255,255,0.12)",
                padding: "5px 18px", borderRadius: 20,
                display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
              }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: GOLD }} />
                <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: 2, textTransform: "uppercase", color: "rgba(255,255,255,0.85)" }}>
                  ⚖️ En línea
                </span>
              </div>
            </div>

            {/* Chat — lado derecho */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

              {/* Cabecera */}
              <div style={{ padding: "10px 16px", borderBottom: `1px solid ${border}`, background: bgPanel, display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                <div style={{ width: 34, height: 34, borderRadius: "50%", overflow: "hidden", border: `2px solid ${GOLD}`, background: GOLD_LIGHT, flexShrink: 0 }}>
                  <img src={CarlaImg} alt="Carla" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top" }} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: textPrimary }}>Carla</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 1 }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e" }} />
                    <span style={{ fontSize: 11, color: textMuted }}>Asesora Jurídica IA · En línea</span>
                  </div>
                </div>
              </div>

              {/* Mensajes */}
              <div ref={chatRef} style={{ flex: 1, overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 6 }}>
                {chatHistory.length === 0 && !chatLoading && (
                  <div style={{ color: textMuted, fontSize: 14, lineHeight: 1.7, marginTop: 8 }}>
                    <div style={{ fontWeight: 700, color: GOLD, fontSize: 16 }}>
                      Hola{nombreCorto ? ` ${nombreCorto}` : ''}, ¿qué necesitas? ⚖️
                    </div>
                  </div>
                )}
                {chatHistory.map((msg, i) => {
                  const esMio = msg.role === "user";
                  const esUltimaCarla = !esMio && i === chatHistory.length - 1;
                  return (
                    <div key={i} ref={esUltimaCarla ? lastCarlaRef : null} style={{ display: "flex", flexDirection: "column", alignItems: esMio ? "flex-end" : "flex-start", marginBottom: 4 }}>
                      <div style={{
                        maxWidth: "88%", padding: "9px 13px",
                        borderRadius: esMio ? "14px 4px 14px 14px" : "4px 14px 14px 14px",
                        background: esMio
                          ? (isDark ? "rgba(201,168,76,0.18)" : "rgba(201,168,76,0.12)")
                          : (isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"),
                        border: esMio ? `1px solid ${GOLD}44` : `1px solid ${border}`,
                      }}>
                        {esMio
                          ? <div style={{ fontSize: 13, color: textPrimary, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{msg.content}</div>
                          : <div style={{ color: textPrimary }}><RenderMensajeCarla text={msg.content} isDark={isDark} /></div>
                        }
                        {msg.sources?.length > 0 && (
                          <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {msg.sources.map((s, si) => (
                              <span key={si} style={{ background: GOLD_LIGHT, color: GOLD, borderRadius: 20, padding: "1px 7px", fontSize: 10, fontWeight: 600 }}>
                                📄 {s.title?.substring(0, 28)}
                              </span>
                            ))}
                          </div>
                        )}
                        <div style={{ fontSize: 10, color: esMio ? GOLD : textMuted, marginTop: 4, textAlign: esMio ? "right" : "left" }}>
                          {msg.created_at ? new Date(msg.created_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) : horaActual()}
                        </div>
                      </div>
                      {/* Opciones rápidas — solo en el último mensaje de Carla y si hay sugerencias */}
                      {esUltimaCarla && msg.suggestions?.length > 0 && !chatLoading && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6, maxWidth: "88%" }}>
                          {msg.suggestions.map((s, si) => (
                            <button key={si}
                              onClick={() => {
                                setChatInput(s);
                                setTimeout(() => {
                                  handleSend(s);
                                }, 50);
                              }}
                              style={{
                                padding: "5px 13px", borderRadius: 20,
                                border: `1.5px solid ${GOLD}`,
                                background: GOLD_LIGHT, color: GOLD,
                                fontSize: 12, fontWeight: 600,
                                cursor: "pointer", fontFamily: "inherit",
                                transition: "all 0.15s",
                              }}
                              onMouseEnter={e => { e.currentTarget.style.background = GOLD; e.currentTarget.style.color = "#fff"; }}
                              onMouseLeave={e => { e.currentTarget.style.background = GOLD_LIGHT; e.currentTarget.style.color = GOLD; }}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                {chatLoading && (
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", overflow: "hidden", border: `1.5px solid ${GOLD}`, background: GOLD_LIGHT, flexShrink: 0 }}>
                      <img src={CarlaImg} style={{ width: "100%", height: "100%", objectFit: "cover" }} alt="Carla" />
                    </div>
                    <div style={{ background: GOLD_LIGHT, border: `1px solid ${GOLD}44`, borderRadius: "4px 14px 14px 14px", padding: "10px 14px", display: "flex", gap: 5 }}>
                      {[0, 1, 2].map(j => (
                        <div key={j} className="dot-gold" style={{ width: 7, height: 7, borderRadius: "50%", background: GOLD, display: "inline-block" }} />
                      ))}
                    </div>
                  </div>
                )}
                <div style={{ height: 4 }} />
              </div>


              {/* Input — siempre visible al fondo */}
              <div style={{ padding: "10px 20px 14px", background: bgPanel, display: "flex", gap: 10, flexShrink: 0 }}>
                <textarea
                  ref={inputRef}
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Escribe tu consulta jurídica... (Enter para enviar)"
                  rows={2}
                  style={{
                    flex: 1, background: inputBg, border: `1px solid ${border}`,
                    borderRadius: 8, padding: "8px 14px", fontSize: 13, color: textPrimary,
                    outline: "none", resize: "none", fontFamily: "inherit", lineHeight: 1.5,
                  }}
                />
                <button onClick={handleSend} disabled={chatLoading || !chatInput.trim()}
                  style={{
                    background: chatLoading || !chatInput.trim() ? GOLD + "55" : GOLD,
                    color: "#fff", border: "none", borderRadius: 8,
                    padding: "10px 20px", fontSize: 13, fontWeight: 700,
                    cursor: chatLoading || !chatInput.trim() ? "not-allowed" : "pointer",
                    fontFamily: "inherit", alignSelf: "flex-end", whiteSpace: "nowrap",
                    transition: "opacity 0.15s",
                  }}>
                  Enviar ⚖️
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── TAB ALERTAS ── */}
        {tab === "Alertas" && (
        <div style={{ flex: 1, overflowY: "auto", background: bgPage }}>
          {alerts.length === 0 ? (
            <div style={{ textAlign: "center", paddingTop: 80, color: textMuted }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🔔</div>
              <div style={{ fontWeight: 600 }}>Sin alertas activas</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Carla monitorea BOE y DOGV semanalmente</div>
            </div>
          ) : (
            <>
              {alerts.filter(a => a.urgency === "alta").length > 0 && (
                <div style={{ padding: "12px 20px 4px", fontSize: 11, fontWeight: 700, color: "#ef4444", textTransform: "uppercase", letterSpacing: "0.06em" }}>🚨 Urgencia Alta</div>
              )}
              {alerts.filter(a => a.urgency === "alta").map(a => <AlertCard key={a.id} alert={a} isDark={isDark} border={border} onAskCarla={handleAskFromAlert} />)}
              {alerts.filter(a => a.urgency === "media").length > 0 && (
                <div style={{ padding: "16px 20px 4px", fontSize: 11, fontWeight: 700, color: "#eab308", textTransform: "uppercase", letterSpacing: "0.06em" }}>⚠️ Urgencia Media</div>
              )}
              {alerts.filter(a => a.urgency === "media").map(a => <AlertCard key={a.id} alert={a} isDark={isDark} border={border} onAskCarla={handleAskFromAlert} />)}
              {alerts.filter(a => a.urgency === "baja").length > 0 && (
                <div style={{ padding: "16px 20px 4px", fontSize: 11, fontWeight: 700, color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.06em" }}>✅ Urgencia Baja</div>
              )}
              {alerts.filter(a => a.urgency === "baja").map(a => <AlertCard key={a.id} alert={a} isDark={isDark} border={border} onAskCarla={handleAskFromAlert} />)}
            </>
          )}
        </div>
        )}

        {/* ── TAB TICKETS ── */}
        {tab === "Tickets" && (
        <div style={{ flex: 1, overflowY: "auto", background: bgPage }}>
          {/* Form nuevo ticket */}
          {showTicketForm && (
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${border}`, background: isDark ? "#0d1017" : "#f8fafc" }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: GOLD, marginBottom: 14 }}>Nueva consulta jurídica</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ fontSize: 11, color: textMuted, display: "block", marginBottom: 4 }}>Categoría</label>
                  <select value={ticketForm.category} onChange={e => setTicketForm(p => ({ ...p, category: e.target.value }))} style={{ ...inputStyle }}>
                    {TICKET_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 11, color: textMuted, display: "block", marginBottom: 4 }}>Prioridad</label>
                  <select value={ticketForm.priority} onChange={e => setTicketForm(p => ({ ...p, priority: e.target.value }))} style={{ ...inputStyle }}>
                    {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, color: textMuted, display: "block", marginBottom: 4 }}>Consulta *</label>
                <textarea value={ticketForm.subject} onChange={e => setTicketForm(p => ({ ...p, subject: e.target.value }))}
                  placeholder="Describe tu consulta jurídica..." rows={3} style={{ ...inputStyle, resize: "none" }} />
              </div>
              {ticketError && <div style={{ color: "#ef4444", fontSize: 12, marginBottom: 10 }}>⚠️ {ticketError}</div>}
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={handleCreateTicket} disabled={loading} style={{ ...btnGold, opacity: loading ? 0.6 : 1 }}>
                  {loading ? "Creando..." : "Crear ticket"}
                </button>
                <button onClick={() => setShowTicketForm(false)} style={{ background: "transparent", border: `1px solid ${border}`, borderRadius: 8, padding: "7px 14px", fontSize: 12, color: textMuted, cursor: "pointer", fontFamily: "inherit" }}>
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {/* Detalle ticket */}
          {selectedTicket && (
            <div style={{ padding: "14px 20px", borderBottom: `1px solid ${GOLD}44`, background: isDark ? "#0d1017" : "#fffbf0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: GOLD }}>{selectedTicket.ticket_ref}</span>
                <button onClick={() => setSelectedTicket(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: textMuted }}>✕</button>
              </div>
              <p style={{ fontWeight: 600, fontSize: 12, color: textPrimary, marginTop: 0 }}>{selectedTicket.subject}</p>
              {selectedTicket.response ? (
                <div style={{ background: isDark ? "#1a1f2e" : "#e8f4fd", borderRadius: 8, padding: 14, fontSize: 12, color: isDark ? "#cbd5e1" : "#334155", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                  {selectedTicket.response}
                </div>
              ) : (
                <div style={{ color: textMuted, fontSize: 12 }}>⏳ Carla está procesando la consulta...</div>
              )}
            </div>
          )}

          {tickets.length === 0 && !showTicketForm ? (
            <div style={{ textAlign: "center", paddingTop: 80, color: textMuted }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🎫</div>
              <div style={{ fontWeight: 600 }}>Sin tickets aún</div>
              <div style={{ fontSize: 12, marginTop: 4 }}>Usa el botón + Nuevo para crear una consulta</div>
            </div>
          ) : (
            tickets.map(tk => (
              <TicketRow key={tk.id} ticket={tk} isDark={isDark} border={border} textPrimary={textPrimary} textMuted={textMuted} onClick={() => setSelectedTicket(tk)} />
            ))
          )}
        </div>
        )}

        {/* ── TAB CONTRATOS ── */}
        {tab === "Contratos" && (
        <div style={{ flex: 1, overflow: "hidden", background: bgPage, display: "flex" }}>
          {/* Form */}
          <div style={{ flex: 1, padding: "20px 24px", borderRight: `1px solid ${border}`, overflowY: "auto" }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: GOLD, marginBottom: 16 }}>⚖️ Generar contrato con IA</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 11, color: textMuted, display: "block", marginBottom: 4 }}>Tipo</label>
                <select value={contractForm.type} onChange={e => {
                  const newType = e.target.value;
                  const validLangs = CONTRACT_LANGS_BY_TYPE[newType];
                  setContractForm(p => ({
                    ...p,
                    type: newType,
                    lang: validLangs.includes(p.lang) ? p.lang : "ES",
                  }));
                }} style={{ ...inputStyle }}>
                  {CONTRACT_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, color: textMuted, display: "block", marginBottom: 4 }}>
                  Idioma
                  {(contractForm.type === "gestion" || contractForm.type === "exclusividad") && (
                    <span style={{ marginLeft: 6, color: "#eab308", fontWeight: 600 }}>· solo ES/EN/DE</span>
                  )}
                </label>
                <select value={contractForm.lang} onChange={e => setContractForm(p => ({ ...p, lang: e.target.value }))} style={{ ...inputStyle }}>
                  {CONTRACT_LANGS_BY_TYPE[contractForm.type].map(l => (
                    <option key={l} value={l}>{LANG_LABELS[l]}</option>
                  ))}
                </select>
              </div>
            </div>
            {[
              { field: "partyA", label: "Vendedor / arrendador *", placeholder: "Nombre y DNI/NIF" },
              { field: "partyB", label: "Comprador / arrendatario *", placeholder: "Nombre y DNI/NIF" },
              { field: "price", label: "Precio / renta", placeholder: "Ej: 250.000 €" },
              { field: "propertyDesc", label: "Inmueble", placeholder: "Dirección y referencia catastral" },
              { field: "signDate", label: "Fecha prevista de firma", placeholder: "" },
            ].map(({ field, label, placeholder }) => (
              <div key={field} style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, color: textMuted, display: "block", marginBottom: 4 }}>{label}</label>
                <input type={field === "signDate" ? "date" : "text"} value={contractForm[field]}
                  onChange={e => setContractForm(p => ({ ...p, [field]: e.target.value }))}
                  placeholder={placeholder} style={{ ...inputStyle }} />
              </div>
            ))}
            {contractError && <div style={{ color: "#ef4444", fontSize: 12, marginBottom: 10 }}>⚠️ {contractError}</div>}
            <button onClick={handleGenerateContract} disabled={loading} style={{ ...btnGold, opacity: loading ? 0.6 : 1 }}>
              {loading ? "Generando..." : "Generar contrato"}
            </button>

            {contracts.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <div style={{ fontWeight: 700, fontSize: 11, color: textMuted, marginBottom: 10, textTransform: "uppercase" }}>Historial</div>
                {contracts.slice(0, 5).map(c => (
                  <div key={c.id} style={{ borderBottom: `1px solid ${border}`, padding: "8px 0", display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                    <span><span style={{ fontWeight: 700, color: GOLD }}>{c.contract_type}</span><span style={{ marginLeft: 8, color: textMuted }}>{c.lang} · {c.party_a}</span></span>
                    <span style={{ color: textMuted, fontSize: 11 }}>{new Date(c.created_at).toLocaleDateString("es-ES")}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Resultado */}
          {contractResult ? (
            <div style={{ flex: 1, padding: "20px 24px", overflowY: "auto", display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: GOLD }}>Contrato generado</span>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {/* Copiar al portapapeles */}
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(contractResult.contractText).then(() => {
                        void dialogs.alert("Contrato copiado al portapapeles");
                      });
                    }}
                    style={{
                      background: "transparent", border: `1px solid ${border}`,
                      borderRadius: 8, padding: "5px 12px", fontSize: 11, color: textMuted,
                      cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5,
                    }}
                  >
                    📋 Copiar texto
                  </button>
                  {/* Descargar como PDF vía ventana de impresión */}
                  <button
                    onClick={() => {
                      const win = window.open("", "_blank");
                      win.document.write(`<!DOCTYPE html><html><head>
                        <meta charset="utf-8">
                        <title>Contrato ${contractForm.type} — Vitalcasa</title>
                        <style>
                          body { font-family: Georgia, serif; font-size: 13px; line-height: 1.8; max-width: 800px; margin: 40px auto; color: #0f172a; }
                          pre { white-space: pre-wrap; word-wrap: break-word; font-family: Georgia, serif; }
                          @media print { body { margin: 20px; } }
                        </style>
                      </head><body><pre>${contractResult.contractText.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</pre></body></html>`);
                      win.document.close();
                      win.focus();
                      setTimeout(() => win.print(), 500);
                    }}
                    style={{
                      background: GOLD, color: "#fff", border: "none",
                      borderRadius: 8, padding: "5px 12px", fontSize: 11, fontWeight: 700,
                      cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5,
                    }}
                  >
                    ⬇ Descargar PDF
                  </button>
                  <button onClick={() => setContractResult(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: textMuted }}>✕</button>
                </div>
              </div>
              {contractResult.warnings && (
                <div style={{ background: "#eab30822", border: "1px solid #eab30844", borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 11, color: "#eab308" }}>⚠️ {contractResult.warnings}</div>
              )}
              <div style={{ fontSize: 13, color: textPrimary, lineHeight: 1.8, whiteSpace: "pre-wrap", fontFamily: "Georgia, serif", flex: 1 }}>
                {contractResult.contractText}
              </div>
            </div>
          ) : (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: textMuted, flexDirection: "column", gap: 8 }}>
              <div style={{ fontSize: 32 }}>📋</div>
              <div style={{ fontSize: 13 }}>El contrato generado aparecerá aquí</div>
            </div>
          )}
        </div>
        )}

        {/* ── TAB BASE DE CONOCIMIENTO ── */}
        {tab === "Base de conocimiento" && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: bgPage }}>
          {/* ── Barra de filtros ── */}
          <div style={{
            background: isDark ? "#0d1117" : "#f1f5f9",
            borderBottom: `2px solid ${GOLD}44`,
            flexShrink: 0,
            boxShadow: isDark ? "0 2px 12px rgba(0,0,0,0.4)" : "0 2px 8px rgba(0,0,0,0.08)",
          }}>
            {/* Fila superior: título + contador + acción */}
            <div style={{ padding: "10px 20px 8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: textPrimary, letterSpacing: "-0.3px" }}>
                  ⚖️ Base jurídica
                </span>
                <span style={{
                  background: GOLD, color: "#fff",
                  borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 800,
                }}>
                  {documents.length} documentos
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {seedStatus && (
                  <span style={{
                    fontSize: 11, color: GOLD,
                    background: GOLD_LIGHT, padding: "3px 10px", borderRadius: 20,
                  }}>{seedStatus}</span>
                )}
                <button
                  onClick={handleSearchNow}
                  disabled={seedLoading || auditLoading}
                  title="Buscar nuevas leyes en BOE/DOGV ahora"
                  style={{
                    display: "flex", alignItems: "center", gap: 5,
                    background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
                    border: `1px solid ${border}`,
                    borderRadius: 8, padding: "5px 12px", fontSize: 11, fontWeight: 600,
                    color: textPrimary, cursor: (seedLoading || auditLoading) ? "not-allowed" : "pointer",
                    fontFamily: "inherit", transition: "all 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; e.currentTarget.style.background = GOLD_LIGHT; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = border; e.currentTarget.style.color = textPrimary; e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"; }}
                >
                  {seedLoading ? "⏳ Buscando..." : "🔍 Buscar novedades BOE"}
                </button>
                <button
                  onClick={handleAudit}
                  disabled={auditLoading || seedLoading}
                  title="Carla analiza su propia base e identifica qué falta"
                  style={{
                    display: "flex", alignItems: "center", gap: 5,
                    background: auditResult ? GOLD_LIGHT : (isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"),
                    border: `1px solid ${auditResult ? GOLD : border}`,
                    borderRadius: 8, padding: "5px 12px", fontSize: 11, fontWeight: 600,
                    color: auditResult ? GOLD : textPrimary,
                    cursor: (auditLoading || seedLoading) ? "not-allowed" : "pointer",
                    fontFamily: "inherit", transition: "all 0.15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; e.currentTarget.style.background = GOLD_LIGHT; }}
                  onMouseLeave={e => { if (!auditResult) { e.currentTarget.style.borderColor = border; e.currentTarget.style.color = textPrimary; e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"; } }}
                >
                  {auditLoading ? "🔄 Analizando..." : "🧠 Auditar base"}
                </button>
                {/* Subir PDF */}
                <button
                  onClick={() => { setShowUploadForm(p => !p); setUploadMsg(null); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 5,
                    background: showUploadForm ? GOLD : (isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"),
                    border: `1px solid ${showUploadForm ? GOLD : border}`,
                    borderRadius: 8, padding: "5px 12px", fontSize: 11, fontWeight: 600,
                    color: showUploadForm ? "#fff" : textPrimary,
                    cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
                  }}
                >
                  📤 Subir PDF
                </button>
                {/* Estado del sistema */}
                <button
                  onClick={() => { setShowStatusPanel(p => !p); if (!systemStatus) loadSystemStatus(); }}
                  title="Ver estado del motor de búsqueda y fuentes monitorizadas"
                  style={{
                    display: "flex", alignItems: "center", gap: 5,
                    background: showStatusPanel ? (isDark ? "rgba(99,179,237,0.15)" : "rgba(66,153,225,0.1)") : (isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"),
                    border: `1px solid ${showStatusPanel ? "#63b3ed" : border}`,
                    borderRadius: 8, padding: "5px 12px", fontSize: 11, fontWeight: 600,
                    color: showStatusPanel ? "#63b3ed" : textPrimary,
                    cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
                  }}
                >
                  📡 Estado
                </button>
              </div>
            </div>

            {/* Panel de estado del sistema */}
            {showStatusPanel && (
              <div style={{
                margin: "0 16px 12px",
                background: isDark ? "#0d1117" : "#f0f7ff",
                border: "1px solid #63b3ed44",
                borderRadius: 10, padding: "14px 16px",
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <span style={{ fontWeight: 700, fontSize: 12, color: "#63b3ed" }}>📡 Estado del motor legislativo</span>
                  <button onClick={loadSystemStatus} disabled={statusLoading}
                    style={{ background: "none", border: "none", color: "#63b3ed", cursor: "pointer", fontSize: 11, fontFamily: "inherit" }}>
                    {statusLoading ? "⏳" : "↻ Actualizar"}
                  </button>
                </div>
                {statusLoading && !systemStatus ? (
                  <div style={{ color: textMuted, fontSize: 11 }}>Cargando...</div>
                ) : systemStatus ? (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    {/* Contadores */}
                    <div>
                      <div style={{ fontSize: 11, color: textMuted, marginBottom: 6 }}>📚 Cobertura</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {[
                          { label: "Docs totales", val: systemStatus.documentos, color: GOLD },
                          { label: "Docs indexados", val: systemStatus.docsIndexados, color: "#48bb78" },
                          { label: "Fragmentos", val: systemStatus.chunks, color: "#63b3ed" },
                          { label: "Alertas activas", val: systemStatus.alertas, color: "#f6ad55" },
                        ].map(({ label, val, color }) => (
                          <div key={label} style={{
                            background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                            border: `1px solid ${color}44`, borderRadius: 8,
                            padding: "5px 10px", textAlign: "center",
                          }}>
                            <div style={{ fontSize: 16, fontWeight: 800, color }}>{val}</div>
                            <div style={{ fontSize: 10, color: textMuted }}>{label}</div>
                          </div>
                        ))}
                      </div>
                      {/* Por categoría */}
                      {systemStatus.porCategoria?.length > 0 && (
                        <div style={{ marginTop: 10 }}>
                          <div style={{ fontSize: 11, color: textMuted, marginBottom: 4 }}>Por tipo:</div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {systemStatus.porCategoria.map(({ categoria, total }) => (
                              <span key={categoria} style={{
                                fontSize: 10, background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                                border: `1px solid ${border}`, borderRadius: 12, padding: "2px 7px",
                              }}>
                                {categoria} ({total})
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      {/* Última actividad */}
                      <div style={{ fontSize: 11, color: textMuted, marginBottom: 6 }}>⏱ Última actividad</div>
                      <div style={{ fontSize: 11, lineHeight: 1.8, color: textPrimary }}>
                        {systemStatus.ultimaBusqueda && (
                          <div>🔍 Búsqueda: <strong>{new Date(systemStatus.ultimaBusqueda).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</strong></div>
                        )}
                        {systemStatus.ultimaAuditoria && (
                          <div>🧠 Auditoría: <strong>{new Date(systemStatus.ultimaAuditoria).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</strong></div>
                        )}
                        {!systemStatus.ultimaBusqueda && !systemStatus.ultimaAuditoria && (
                          <div style={{ color: textMuted }}>Sin actividad registrada</div>
                        )}
                      </div>
                      {/* Fuentes monitorizadas */}
                      <div style={{ fontSize: 11, color: textMuted, marginTop: 8, marginBottom: 4 }}>📡 Fuentes monitorizadas</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {(systemStatus.fuentesMonitorizadas || []).map((f, i) => (
                          <div key={i} style={{ fontSize: 10, color: textPrimary }}>✓ {f}</div>
                        ))}
                      </div>
                      {/* Resumen última auditoría */}
                      {systemStatus.resumenAuditoria && (
                        <div style={{ marginTop: 8 }}>
                          <div style={{ fontSize: 11, color: textMuted, marginBottom: 4 }}>📋 Último informe de auditoría</div>
                          <div style={{
                            fontSize: 10, color: textPrimary, background: isDark ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.05)",
                            borderRadius: 6, padding: "6px 8px", maxHeight: 80, overflowY: "auto",
                            whiteSpace: "pre-wrap",
                          }}>
                            {systemStatus.resumenAuditoria}
                          </div>
                        </div>
                      )}
                      <button onClick={handleAutoAudit}
                        style={{
                          marginTop: 8, background: "none", border: "1px solid #63b3ed44",
                          borderRadius: 6, padding: "4px 10px", fontSize: 10, color: "#63b3ed",
                          cursor: "pointer", fontFamily: "inherit",
                        }}>
                        🧠 Lanzar auditoría ahora
                      </button>
                      {autoAuditMsg && <div style={{ fontSize: 10, color: GOLD, marginTop: 4 }}>{autoAuditMsg}</div>}
                    </div>
                  </div>
                ) : null}
              </div>
            )}

            {/* Formulario subir PDF */}
            {showUploadForm && (
              <div style={{
                margin: "0 16px 12px",
                background: isDark ? "#0d1117" : "#f8fafc",
                border: `1px solid ${GOLD}55`,
                borderRadius: 10, padding: "14px 16px",
              }}>
                <div style={{ fontWeight: 700, fontSize: 12, color: GOLD, marginBottom: 12 }}>📤 Subir nuevo documento PDF</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                  <div>
                    <label style={{ fontSize: 11, color: textMuted, display: "block", marginBottom: 3 }}>Título *</label>
                    <input value={uploadForm.title} onChange={e => setUploadForm(p => ({ ...p, title: e.target.value }))}
                      placeholder="Nombre del documento" style={{ ...inputStyle }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: textMuted, display: "block", marginBottom: 3 }}>Archivo PDF *</label>
                    <input type="file" accept=".pdf" onChange={e => setUploadFile(e.target.files[0])}
                      style={{ ...inputStyle, padding: "5px 8px" }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: textMuted, display: "block", marginBottom: 3 }}>Categoría</label>
                    <select value={uploadForm.category} onChange={e => setUploadForm(p => ({ ...p, category: e.target.value }))} style={{ ...inputStyle }}>
                      {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: textMuted, display: "block", marginBottom: 3 }}>Fuente</label>
                    <input value={uploadForm.source} onChange={e => setUploadForm(p => ({ ...p, source: e.target.value }))}
                      placeholder="BOE, DOGV, Vitalcasa..." style={{ ...inputStyle }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: textMuted, display: "block", marginBottom: 3 }}>Idioma</label>
                    <select value={uploadForm.lang} onChange={e => setUploadForm(p => ({ ...p, lang: e.target.value }))} style={{ ...inputStyle }}>
                      {["ES","EN","DE","FR","NL","RU"].map(l => <option key={l} value={l}>{LANG_LABELS[l]}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: textMuted, display: "block", marginBottom: 3 }}>Fecha del documento</label>
                    <input type="date" value={uploadForm.doc_date} onChange={e => setUploadForm(p => ({ ...p, doc_date: e.target.value }))}
                      style={{ ...inputStyle }} />
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                  <input type="checkbox" id="isOwn" checked={uploadForm.is_own}
                    onChange={e => setUploadForm(p => ({ ...p, is_own: e.target.checked }))} />
                  <label htmlFor="isOwn" style={{ fontSize: 12, color: textMuted, cursor: "pointer" }}>Documento propio de Vitalcasa</label>
                </div>
                {uploadMsg && (
                  <div style={{ fontSize: 12, color: uploadMsg.startsWith("✅") ? "#22c55e" : "#ef4444", marginBottom: 10 }}>
                    {uploadMsg}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={handleUploadPdf} disabled={uploadLoading} style={{ ...btnGold, opacity: uploadLoading ? 0.6 : 1 }}>
                    {uploadLoading ? "⏳ Subiendo..." : "📤 Subir e indexar"}
                  </button>
                  <button onClick={() => { setShowUploadForm(false); setUploadMsg(null); }}
                    style={{ background: "transparent", border: `1px solid ${border}`, borderRadius: 8, padding: "7px 14px", fontSize: 12, color: textMuted, cursor: "pointer", fontFamily: "inherit" }}>
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* Fila filtros — wrap para que todos sean siempre visibles */}
            <div style={{
              padding: "0 16px 12px",
              display: "flex", flexWrap: "wrap", gap: 6,
            }}>
              {DOC_FILTERS.filter(f =>
                f.id === "all" || documents.some(d => d.category === f.id)
              ).map(f => {
                const count = f.id === "all" ? documents.length : documents.filter(d => d.category === f.id).length;
                const active = docCategory === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => setDocCategory(f.id)}
                    style={{
                      flexShrink: 0,
                      display: "flex", alignItems: "center", gap: 6,
                      background: active
                        ? GOLD
                        : isDark ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.9)",
                      color: active ? "#fff" : textPrimary,
                      border: `1.5px solid ${active ? GOLD : (isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)")}`,
                      borderRadius: 10, padding: "6px 14px",
                      fontSize: 12, fontWeight: active ? 700 : 500,
                      cursor: "pointer", fontFamily: "inherit",
                      transition: "all 0.15s",
                      boxShadow: active ? `0 2px 8px ${GOLD}55` : "none",
                    }}
                    onMouseEnter={e => { if (!active) { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; e.currentTarget.style.background = GOLD_LIGHT; } }}
                    onMouseLeave={e => { if (!active) { e.currentTarget.style.borderColor = isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"; e.currentTarget.style.color = textPrimary; e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.07)" : "rgba(255,255,255,0.9)"; } }}
                  >
                    <span style={{ fontSize: 14 }}>{f.icon}</span>
                    <span>{f.label}</span>
                    <span style={{
                      background: active ? "rgba(255,255,255,0.3)" : GOLD_LIGHT,
                      color: active ? "#fff" : GOLD,
                      borderRadius: 20, padding: "1px 7px",
                      fontSize: 11, fontWeight: 800, minWidth: 20, textAlign: "center",
                    }}>{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Panel auditoría — aparece cuando hay resultado */}
          {(auditLoading || auditResult) && (
            <div style={{
              margin: "12px 20px",
              borderRadius: 12,
              border: `1px solid ${GOLD}55`,
              background: isDark ? "rgba(201,168,76,0.07)" : "rgba(201,168,76,0.06)",
              overflow: "hidden",
              flexShrink: 0,
            }}>
              {/* Cabecera panel */}
              <div style={{
                padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center",
                borderBottom: `1px solid ${GOLD}33`,
                background: isDark ? "rgba(201,168,76,0.12)" : "rgba(201,168,76,0.1)",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 16 }}>🧠</span>
                  <span style={{ fontWeight: 700, fontSize: 13, color: GOLD }}>
                    Auditoría de la base — análisis de Carla
                  </span>
                  {auditResult?.docs_analizados && (
                    <span style={{ fontSize: 11, color: textMuted }}>
                      ({auditResult.docs_analizados} docs analizados)
                    </span>
                  )}
                </div>
                {auditResult && (
                  <button
                    onClick={() => setAuditResult(null)}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: textMuted, lineHeight: 1 }}
                  >✕</button>
                )}
              </div>
              {/* Contenido */}
              <div style={{ padding: "14px 18px", maxHeight: 420, overflowY: "auto" }}>
                {auditLoading ? (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, padding: "20px 0", color: textMuted }}>
                    <div style={{ display: "flex", gap: 5 }}>
                      {[0, 1, 2].map(i => (
                        <span key={i} className="dot-gold" style={{ width: 8, height: 8, borderRadius: "50%", background: GOLD, display: "inline-block" }} />
                      ))}
                    </div>
                    <span style={{ fontSize: 12 }}>Carla está revisando su base de conocimiento...</span>
                  </div>
                ) : auditResult?.error ? (
                  <div style={{ color: "#ef4444", fontSize: 12 }}>❌ {auditResult.error}</div>
                ) : auditResult?.audit ? (
                  <div style={{ fontSize: 12, color: textPrimary, lineHeight: 1.9, whiteSpace: "pre-wrap" }}>
                    {auditResult.audit}
                  </div>
                ) : null}
              </div>
            </div>
          )}

          <div style={{ flex: 1, overflowY: "auto" }}>
            {filteredDocs.length === 0 ? (
              <div style={{ textAlign: "center", paddingTop: 60, color: textMuted, maxWidth: 420, margin: "0 auto" }}>
                <div style={{ fontSize: 40, marginBottom: 16 }}>📚</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: textPrimary, marginBottom: 8 }}>Base de conocimiento vacía</div>
                <div style={{ fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>
                  Carla necesita cargar los documentos jurídicos base para poder responder consultas.
                  Haz clic en "Inicializar base" para cargar las 18 normas de partida.
                </div>
                <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                  <button onClick={handleSeed} disabled={seedLoading}
                    style={{ background: GOLD, color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontSize: 13, fontWeight: 700, cursor: seedLoading ? "not-allowed" : "pointer", opacity: seedLoading ? 0.7 : 1, fontFamily: "inherit" }}>
                    {seedLoading ? "Cargando..." : "⚖️ Inicializar base (18 docs)"}
                  </button>
                  <button onClick={handleSearchNow} disabled={seedLoading}
                    style={{ background: "transparent", color: GOLD, border: `1px solid ${GOLD}`, borderRadius: 8, padding: "10px 16px", fontSize: 13, fontWeight: 600, cursor: seedLoading ? "not-allowed" : "pointer", opacity: seedLoading ? 0.7 : 1, fontFamily: "inherit" }}>
                    🔍 Buscar novedades BOE
                  </button>
                </div>
                {seedStatus && (
                  <div style={{ marginTop: 14, fontSize: 12, color: seedStatus.startsWith("✅") ? "#22c55e" : seedStatus.startsWith("❌") ? "#ef4444" : GOLD, background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)", borderRadius: 8, padding: "8px 14px" }}>
                    {seedStatus}
                  </div>
                )}
              </div>
            ) : (
              filteredDocs.map(doc => (
                <DocumentRow key={doc.id} doc={doc} isDark={isDark} border={border} textPrimary={textPrimary} textMuted={textMuted} />
              ))
            )}
          </div>
        </div>
        )}

      </div>
    </div>
  );
}
